import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useLocaleStore } from '../lib/locale-store';
import { getEnumValues, isEnumColumn } from '../lib/enum-introspect';
import { RELATIONSHIPS } from '../lib/relationships';
import type { ColumnType } from '../lib/table-columns';
import { translateKey } from '../lib/translations';
import type { EnvSchema } from '../EnvSelector';
import type { TableName } from '../TableSidebar';
import { FkCombobox } from './FkCombobox';
import { FkMultiSelect, type FkMultiSelectOption } from './FkMultiSelect';
import { readAuthToken } from '@/auth/auth-token';
import { uploadProductImage } from '../lib/upload-image';

interface Props {
  type: ColumnType;
  value: unknown;
  onChange: (next: unknown) => void;
  /** When false, the cell is read-only (renders the formatted value, no input). */
  editing: boolean;
  /** When set, displayed as the input's `aria-label`. */
  ariaLabel?: string;
  /** Owning table — required so the cell can detect FK columns. */
  table?: TableName;
  /** Column name — required so the cell can detect FK columns. */
  column?: string;
  /** Pre-fetched FK option rows keyed by parent ref-table name. */
  fkOptions?: Record<string, Record<string, unknown>[]>;
  /** When true, the cell renders a non-editable composite-PK marker. */
  isCompositePkColumn?: boolean;
  /** Schema env — required for enum-column auto-detection. */
  env?: EnvSchema;
}

/**
 * Polymorphic cell — switches between text / number / boolean / JSON / int[] / image
 * widget based on the column's declared `ColumnType`. When `editing` is
 * false, falls back to a compact read-only renderer.
 *
 * In edit mode, FK columns (declared in `RELATIONSHIPS`) take precedence
 * and render as `FkCombobox` (single value) or `FkMultiSelect` (array).
 * Composite-PK columns are locked.
 */
export function EditableCell({
  type,
  value,
  onChange,
  editing,
  ariaLabel,
  table,
  column,
  fkOptions,
  isCompositePkColumn,
  env,
}: Props) {
  if (!editing) return <ReadOnlyCell type={type} value={value} />;

  if (isCompositePkColumn) {
    return (
      <span className="text-xs text-muted-foreground italic">(composite PK — not editable)</span>
    );
  }

  if (env && table && column && isEnumColumn(env, table, column)) {
    const values = getEnumValues(env, table, column) ?? [];
    return (
      <FkCombobox
        options={values.map((v) => ({ value: v, label: v }))}
        value={(value ?? null) as string | null}
        onChange={(v) => onChange(v)}
      />
    );
  }

  const fk =
    table && column ? RELATIONSHIPS[table].parents.find((p) => p.column === column) : undefined;

  if (fk) {
    const parentRows = fkOptions?.[fk.refTable] ?? [];
    const opts = parentRows.map((r) => ({
      value: r[fk.refColumn] as string | number,
      label: `${r[fk.refColumn]} — ${fk.labelColumns
        .map((lc) => r[lc])
        .filter(Boolean)
        .join(' / ')}`,
    }));

    if (fk.isArray) {
      return (
        <FkMultiSelect
          options={opts as FkMultiSelectOption[]}
          value={Array.isArray(value) ? (value as number[]) : []}
          onChange={(v) => onChange(v)}
        />
      );
    }
    return (
      <FkCombobox
        options={opts}
        value={value as string | number | null}
        onChange={(v) => onChange(v)}
        allowFreeForm={fk.isText}
      />
    );
  }

  switch (type) {
    case 'integer':
      return <NumberCell value={value} onChange={onChange} ariaLabel={ariaLabel} integer />;
    case 'numeric':
      return <NumberCell value={value} onChange={onChange} ariaLabel={ariaLabel} />;
    case 'boolean':
      return <BooleanCell value={value} onChange={onChange} ariaLabel={ariaLabel} />;
    case 'jsonb':
      return <JsonCell value={value} onChange={onChange} ariaLabel={ariaLabel} />;
    case 'integer[]':
      return <IntArrayCell value={value} onChange={onChange} ariaLabel={ariaLabel} />;
    case 'image':
      return <ImageCell value={value} onChange={onChange} ariaLabel={ariaLabel} />;
    default:
      return <TextCell value={value} onChange={onChange} ariaLabel={ariaLabel} />;
  }
}

function ReadOnlyCell({ type, value }: { type: ColumnType; value: unknown }) {
  const localeMode = useLocaleStore((s) => s.mode);
  // Subscribe to dataVersion so the cell re-renders when translations finish
  // loading. The `cache` in translations.ts is a module variable; without this
  // subscription we'd only re-render on toggle change, not on fetch completion.
  useLocaleStore((s) => s.dataVersion);
  if (value == null) return <span className="text-muted-foreground">—</span>;
  if (type === 'image') {
    return typeof value === 'string' && value ? (
      <img src={value} alt="" className="h-8 w-8 rounded border object-cover" />
    ) : (
      <span className="text-muted-foreground">—</span>
    );
  }
  if (type === 'jsonb' || type === 'integer[]' || typeof value === 'object') {
    const s = JSON.stringify(value);
    return (
      <span className="font-mono text-xs" title={s}>
        {s.length > 60 ? `${s.slice(0, 57)}…` : s}
      </span>
    );
  }
  if (type === 'boolean') {
    return <span className="font-mono text-xs">{value ? 'true' : 'false'}</span>;
  }
  const raw = String(value);
  const displayed =
    localeMode === 'KEY' ? raw : translateKey(raw, localeMode === 'EN' ? 'en' : 'ko');
  return (
    <span className="text-xs" title={raw}>
      {displayed.length > 80 ? `${displayed.slice(0, 77)}…` : displayed}
    </span>
  );
}

function TextCell({
  value,
  onChange,
  ariaLabel,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  ariaLabel?: string;
}) {
  return (
    <Input
      type="text"
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
      aria-label={ariaLabel}
      className="h-7 px-2 text-xs"
    />
  );
}

function NumberCell({
  value,
  onChange,
  ariaLabel,
  integer,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  ariaLabel?: string;
  integer?: boolean;
}) {
  return (
    <Input
      type="number"
      step={integer ? 1 : 'any'}
      value={value == null ? '' : String(value)}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === '') {
          onChange(null);
          return;
        }
        const n = integer ? Number.parseInt(raw, 10) : Number.parseFloat(raw);
        onChange(Number.isNaN(n) ? raw : n);
      }}
      aria-label={ariaLabel}
      className="h-7 px-2 text-right text-xs"
    />
  );
}

function BooleanCell({
  value,
  onChange,
  ariaLabel,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  ariaLabel?: string;
}) {
  return (
    <Checkbox
      checked={value === true}
      onCheckedChange={(c) => onChange(c === true)}
      aria-label={ariaLabel}
    />
  );
}

function JsonCell({
  value,
  onChange,
  ariaLabel,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  ariaLabel?: string;
}) {
  const initial = value == null ? '' : JSON.stringify(value, null, 2);
  const [text, setText] = useState(initial);
  const [err, setErr] = useState<string | null>(null);
  const lastValue = useRef(initial);

  useEffect(() => {
    const incoming = value == null ? '' : JSON.stringify(value, null, 2);
    if (incoming !== lastValue.current) {
      setText(incoming);
      lastValue.current = incoming;
      setErr(null);
    }
  }, [value]);

  return (
    <div className="flex flex-col gap-1">
      <Textarea
        value={text}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          if (next.trim() === '') {
            setErr(null);
            onChange(null);
            return;
          }
          try {
            const parsed = JSON.parse(next);
            setErr(null);
            onChange(parsed);
          } catch (parseErr) {
            setErr((parseErr as Error).message);
          }
        }}
        aria-label={ariaLabel}
        className="min-h-[60px] font-mono text-[11px] leading-tight"
        rows={3}
      />
      {err && <span className="text-[10px] text-rose-600 dark:text-rose-400">JSON: {err}</span>}
    </div>
  );
}

function IntArrayCell({
  value,
  onChange,
  ariaLabel,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  ariaLabel?: string;
}) {
  const initial = Array.isArray(value) ? value.join(',') : '';
  const [text, setText] = useState(initial);
  const [err, setErr] = useState<string | null>(null);
  const lastValue = useRef(initial);

  useEffect(() => {
    const incoming = Array.isArray(value) ? value.join(',') : '';
    if (incoming !== lastValue.current) {
      setText(incoming);
      lastValue.current = incoming;
      setErr(null);
    }
  }, [value]);

  return (
    <div className="flex flex-col gap-1">
      <Input
        value={text}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          if (next.trim() === '') {
            setErr(null);
            onChange([]);
            return;
          }
          const parts = next
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
          const nums = parts.map((p) => Number.parseInt(p, 10));
          if (nums.some((n) => Number.isNaN(n))) {
            setErr('integers only');
            return;
          }
          setErr(null);
          onChange(nums);
        }}
        aria-label={ariaLabel}
        placeholder="1,2,3"
        className="h-7 px-2 text-xs"
      />
      {err && <span className="text-[10px] text-rose-600 dark:text-rose-400">{err}</span>}
    </div>
  );
}

function ImageCell({
  value,
  onChange,
  ariaLabel,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  ariaLabel?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const url = typeof value === 'string' ? value : '';

  async function onPick(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true);
    setErr(null);
    try {
      const token = readAuthToken();
      if (!token) throw new Error('로그인 토큰이 없습니다.');
      const next = await uploadProductImage(f, token);
      onChange(next);
    } catch (e2) {
      setErr((e2 as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-1">
      {url ? (
        <img src={url} alt="" className="h-10 w-10 rounded border object-cover" />
      ) : (
        <span className="text-muted-foreground text-xs">— 이미지 없음</span>
      )}
      <Input
        type="text"
        value={url}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        aria-label={ariaLabel}
        placeholder="https://..."
        className="h-7 px-2 text-xs"
      />
      <label className="inline-flex">
        <input
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPick}
          disabled={busy}
          aria-label={`${ariaLabel ?? 'image'} upload`}
        />
        <span className="cursor-pointer rounded border px-2 py-0.5 text-[11px]">
          {busy ? '업로드 중…' : '업로드'}
        </span>
      </label>
      {err && <span className="text-[10px] text-rose-600 dark:text-rose-400">{err}</span>}
    </div>
  );
}
